// @flow
// Maintenance-mode gate.
//
// Enabled per-environment via the REACT_APP_MAINTENANCE_MODE build flag
// (see .env / .env.staging / .env.production). Because this is baked in at
// build time, toggling it requires a rebuild + redeploy.
//
// A bypass mechanism lets trusted users view the live site while maintenance
// is on: visit any URL with ?bypass=<REACT_APP_MAINTENANCE_BYPASS> once and a
// flag is stored in localStorage so subsequent navigation works without the
// param. Visit ?bypass=clear to remove it.

const BYPASS_STORAGE_KEY = 'nrp_maintenance_bypass';

const getQueryParam = (name: string): string | null => {
    // Avoid URLSearchParams for older-browser safety (app targets React 15).
    const match = window.location.search.match(
        new RegExp('[?&]' + name + '=([^&]*)')
    );
    return match ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : null;
};

const safeStorage = {
    get(key: string): string | null {
        try {
            return window.localStorage.getItem(key);
        } catch (e) {
            return null; // private mode / storage disabled
        }
    },
    set(key: string, value: string) {
        try {
            window.localStorage.setItem(key, value);
        } catch (e) {
            /* no-op */
        }
    },
    remove(key: string) {
        try {
            window.localStorage.removeItem(key);
        } catch (e) {
            /* no-op */
        }
    },
};

// Returns true if the current user should skip maintenance mode.
// Processes the ?bypass= param as a side effect (stores/clears the flag).
export const isMaintenanceBypassed = (): boolean => {
    const secret = process.env.REACT_APP_MAINTENANCE_BYPASS;
    const param = getQueryParam('bypass');

    if (param === 'clear') {
        safeStorage.remove(BYPASS_STORAGE_KEY);
        return false;
    }

    // Only honour a non-empty configured secret. This prevents an unset
    // REACT_APP_MAINTENANCE_BYPASS from being bypassable with ?bypass= (empty).
    if (secret && param === secret) {
        safeStorage.set(BYPASS_STORAGE_KEY, '1');
        return true;
    }

    return safeStorage.get(BYPASS_STORAGE_KEY) === '1';
};

// Returns true when the app should render the maintenance screen instead of
// the full application.
export const isMaintenanceMode = (): boolean => {
    if (process.env.REACT_APP_MAINTENANCE_MODE !== 'true') {
        return false;
    }
    return !isMaintenanceBypassed();
};



// WEBPACK FOOTER //
// ./src/shared/maintenance.js